import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfigurableDrawer from "./ConfigurableDrawer";
import { Badge, Btn, dt } from "./common";
import { toast } from "sonner";
import {
  ExternalLink, MessageSquare, AlertTriangle, Clock, Send, Copy,
} from "lucide-react";

const FIELD_DEFS = [
  { key: "referencia", label: "Referencia", render: (t) => <code className="text-[11px] font-mono">{t.referencia || (t.id || "").slice(0, 8)}</code> },
  { key: "organizacao_nome", label: "Cliente", render: (t) => t.organizacao_nome || "-" },
  { key: "estado", label: "Estado", render: (t) => <Badge value={t.estado} /> },
  { key: "prioridade", label: "Prioridade", render: (t) => <Badge value={t.prioridade} /> },
  { key: "origem", label: "Origem", render: (t) => t.origem || "-" },
  { key: "responsavel_email", label: "Responsavel", render: (t) => t.responsavel_nome || t.responsavel_email || "-" },
  { key: "criado_em", label: "Criado", render: (t) => t.criado_em ? dt(t.criado_em) : "-" },
  { key: "atualizado_em", label: "Atualizado", render: (t) => t.atualizado_em ? dt(t.atualizado_em) : "-" },
  { key: "sla", label: "SLA", render: (t) => (
    t.sla_excedido ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 rounded-full px-2 py-0.5"><AlertTriangle size={10} /> excedido</span>
    : (t.sla_restante_horas != null ? <span className="text-[11px] text-slate-600"><Clock size={10} className="inline" /> {t.sla_restante_horas}h</span> : "-")
  ) },
];

const NEXT = {
  aberto: ["em_curso", "fechado"],
  em_curso: ["a_aguardar_cliente", "resolvido"],
  a_aguardar_cliente: ["em_curso", "resolvido"],
  resolvido: ["fechado", "em_curso"],
};

const DEFAULT_ACTIONS = ["responder", "copiar_ref", "email_cliente"];
const DEFAULT_ACTIONS_VISIBLE = { responder: true, copiar_ref: true, email_cliente: false };

function makeActionDefs(onOpenFull, nav) {
  return [
    { key: "responder", label: "Responder", icon: MessageSquare, enabled: () => true,
      run: (t) => onOpenFull && onOpenFull(t) },
    { key: "copiar_ref", label: "Copiar ref.", icon: Copy, enabled: (t) => !!(t.referencia || t.id),
      run: async (t) => {
        const ref = t.referencia || t.id;
        try { await navigator.clipboard.writeText(ref); toast.success(`Ref ${ref} copiada.`); }
        catch { toast.error("Nao foi possivel copiar."); }
      } },
    { key: "email_cliente", label: "Email cliente", icon: Send, enabled: (t) => !!(t.contacto_email || t.email),
      run: (t) => {
        const em = t.contacto_email || t.email;
        if (!em) return toast.message("Sem email registado.");
        if (t.organizacao_id) {
          nav(`/app/crm/contas/${t.organizacao_id}?tab=emails&compose=1&email_to=${encodeURIComponent(em)}&assunto=${encodeURIComponent(`Ticket ${t.referencia || t.id}: ${t.assunto || ""}`)}`);
        } else {
          // Sem organizacao_id: mantem mailto como ultimo recurso
          window.location.href = `mailto:${em}?subject=${encodeURIComponent(`Ticket ${t.referencia || t.id}: ${t.assunto || ""}`)}`;
        }
      } },
  ];
}

export default function TicketDrawer({ ticket, open, onClose, onTransition, onOpenFull }) {
  const nav = useNavigate();
  const [detail, setDetail] = useState(ticket || null);
  useEffect(() => { if (open && ticket) setDetail(ticket); }, [open, ticket]);
  const t = detail || ticket || {};
  const nextStates = NEXT[t.estado] || [];
  const placeholders = {
    id: t.id || "", referencia: t.referencia || "", assunto: t.assunto || "",
    cliente: t.organizacao_nome || "", email: t.contacto_email || t.email || "",
    prioridade: t.prioridade || "", estado: t.estado || "", origem: t.origem || "",
    responsavel_email: t.responsavel_email || "", responsavel_nome: t.responsavel_nome || "",
  };
  const msgs = t.mensagens || [];

  const extraSections = [
    ...(t.descricao ? [{
      key: "descricao", label: "Descricao", defaultVisible: true,
      render: () => <div className="text-[13px] text-navy whitespace-pre-wrap">{t.descricao}</div>,
    }] : []),
    ...(msgs.length > 0 ? [{
      key: "mensagens", label: `Mensagens (${msgs.length})`, defaultVisible: true,
      render: () => (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {msgs.slice(-5).map((m, i) => (
            <div key={i} className={`rounded-md p-2 text-[12px] ${m.tipo === "agente" ? "bg-blue/10 ml-4" : "bg-slate-50 mr-4"}`}>
              <div className="text-[10px] text-slate-500 mb-0.5">{m.tipo === "agente" ? "Agente" : "Cliente"} - {m.autor} - {dt(m.em)}</div>
              <div className="text-navy line-clamp-3" dangerouslySetInnerHTML={{ __html: m.mensagem || "" }} />
            </div>
          ))}
          {msgs.length > 5 && <div className="text-[11px] text-slate-500 text-center">+ {msgs.length - 5} mensagens antigas</div>}
        </div>
      ),
    }] : []),
  ];

  return (
    <ConfigurableDrawer
      open={open} onClose={onClose}
      overline="Service Desk"
      title={t.assunto || "Ticket"}
      testid="ticket-drawer"
      uiPrefKey="ticket_drawer" uiPrefVersion="v1"
      fieldDefs={FIELD_DEFS}
      actionDefs={makeActionDefs(onOpenFull, nav)}
      defaultActions={DEFAULT_ACTIONS}
      defaultActionsVisible={DEFAULT_ACTIONS_VISIBLE}
      extraSections={extraSections}
      entity={t}
      placeholders={placeholders}
      transitions={onTransition ? nextStates.map((s) => ({
        key: s, label: s.replace(/_/g, " "),
        onClick: () => onTransition(t.id, s),
      })) : []}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} testid="ticket-drawer-close-btn">Fechar</Btn>
          {onOpenFull && <Btn onClick={() => onOpenFull(t)} testid="ticket-drawer-full-btn"><ExternalLink size={14} /> Abrir para responder</Btn>}
        </>
      }
    />
  );
}
